import React, { useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "components/Common/Breadcrumb";
import { Container, Card, CardBody, Row, Col, Button } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "utils/useHotToast";
import { ReactComponent as EditIcon } from "assets/icons/edit.svg";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";

// API
import { useAddAdminMutation, useGetAdminQuery, useUpdateAdminMutation } from "services/admins/api";
import { simpleObjectToFormData } from "utils/formData";

// Schema
import InfoForm from "../../Profile/info/form";
import InfoView from "../../Profile/info/view";
import UserForm from "../../Profile/user/form";
import UserView from "../../Profile/user/view";

import AddressView from "../../Profile/address/view";
import AddressForm from "../../Profile/address/form";

const AdminForm = () => {
  const infoFormRef = useRef(null);
  const userFormRef = useRef(null);
  const addressFormRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const isEditMode = !!id;

  const [addAdmin] = useAddAdminMutation();
  const [updateAdmin] = useUpdateAdminMutation();
  const { data: user } = useGetAdminQuery(id, { skip: !isEditMode });

  const title = isEditMode ? "แก้ไขข้อมูลแอดมิน" : "เพิ่มแอดมินผู้ใช้งาน";
  document.title = `${title} | ${process.env.REACT_APP_CMS_TITLE}`;
  const navigate = useNavigate();
  const { addToast, removeToast } = useToasts();

  const [expandedCards, setExpandedCards] = useState({
    info: isEditMode ? false : true,
    user: isEditMode ? false : true,
    address: isEditMode ? false : true,
  });

  const handleCancel = () => {
    navigate("/admin");
  };

  // Handle InfoForm submission
  const handleInfoSubmit = async (values) => {
    if (!id) return; // Shouldn't happen in edit mode

    try {
      const result = await updateAdmin({
        id,
        ...values,
      });

      const toastId = addToast(<ToastNotification icon={check} title="บันทึกข้อมูลสำเร็จ" description="ข้อมูลส่วนตัวได้รับการอัปเดตเรียบร้อยแล้ว" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, { autoDismiss: true, appearance: "success" });

      // Switch back to view mode
      setExpandedCards((prev) => ({ ...prev, info: false }));
      return result;
    } catch (error) {
      const errorMessage = error?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง";
      const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => removeToast(toastId)} variant="danger" />, { autoDismiss: false, appearance: "error" });
      throw error;
    }
  };

  const handleAddressSubmit = async (values) => {
    if (!id) return; // Shouldn't happen in edit mode

    try {
      const result = await updateAdmin({
        id,
        addresses: [values],
      });

      const toastId = addToast(<ToastNotification icon={check} title="บันทึกข้อมูลสำเร็จ" description="ข้อมูลส่วนตัวได้รับการอัปเดตเรียบร้อยแล้ว" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, { autoDismiss: true, appearance: "success" });

      // Switch back to view mode
      setExpandedCards((prev) => ({ ...prev, address: false }));
      return result;
    } catch (error) {
      const errorMessage = error?.data?.message || "ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง";
      const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description={errorMessage} onSecondaryClick={() => removeToast(toastId)} variant="danger" />, { autoDismiss: false, appearance: "error" });
      throw error;
    }
  };

  return (
    <div className="page-content" data-testid="profile-form-page">
      <Container fluid>
        <Breadcrumbs title={title} breadcrumbItem={title} back="กลับหน้าจัดการแอดมินผู้ใช้งาน" />
        <div className="contents-tab" data-testid="tab-contents">
          <>
            {/* Info */}
            <Card className="card-form mt-4">
              <CardBody>
                <div className="flex gap-3">
                  <div className="flex-grow-1 w-full ms-3">
                    <Row>
                      <Col sm={12} className="d-flex justify-content-between">
                        <h2 className="fw-semibold text-gray-700">ข้อมูลส่วนตัว</h2>
                        {isEditMode && (
                          <button className="btn" onClick={() => setExpandedCards({ ...expandedCards, info: !expandedCards.info })}>
                            <EditIcon />
                          </button>
                        )}
                      </Col>
                    </Row>
                    {expandedCards.info ? (
                      <InfoForm
                        data={user}
                        onSwitchView={() => setExpandedCards((prev) => ({ ...prev, info: false }))}
                        isEditMode={isEditMode}
                        formRef={infoFormRef}
                        onSubmit={handleInfoSubmit}
                      />
                    ) : (
                      <InfoView data={user} />
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* User */}
            <Card className="card-form mt-4">
              <CardBody>
                <div className="flex gap-3">
                  <div className="flex-grow-1 w-full ms-3">
                    <Row>
                      <Col sm={12} className="d-flex justify-content-between">
                        <h2 className="fw-semibold text-gray-700">ไอดีของแอดมิน</h2>
                      </Col>
                    </Row>
                    {expandedCards.user ? <UserForm data={user} onSwitchView={() => setExpandedCards({ ...expandedCards, user: false })} isEditMode={isEditMode} isAdmin={true} formRef={userFormRef} /> : <UserView data={user} />}
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Address */}
            {isEditMode && (
              <Card className="card-form mt-4">
                <CardBody>
                  <div className="flex gap-3">
                    <div className="flex-grow-1 w-full ms-3">
                      <Row>
                        <Col sm={12} className="d-flex justify-content-between">
                          <h2 className="fw-semibold text-gray-700">ที่อยู่</h2>
                          {isEditMode && (
                            <button className="btn" onClick={() => setExpandedCards({ ...expandedCards, address: !expandedCards.address })}>
                              <EditIcon />
                            </button>
                          )}
                        </Col>
                      </Row>
                      {expandedCards.address ? (
                        <AddressForm
                          //
                          data={user}
                          onSwitchView={() => setExpandedCards({ ...expandedCards, address: false })}
                          isEditMode={isEditMode}
                          formRef={addressFormRef}
                          onSubmit={handleAddressSubmit}
                        />
                      ) : (
                        <AddressView data={user} />
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        </div>
        {!isEditMode && (
          <div className="text-end my-4">
            <div className="d-flex justify-content-between gap-3 w-100">
              <Button color="light" outline className="w-52" onClick={handleCancel}>
                ยกเลิก
              </Button>
              <Button
                className="w-52"
                color="primary"
                type="button"
                disabled={isSubmitting}
                onClick={async () => {
                  if (infoFormRef.current && userFormRef.current) {
                    setIsSubmitting(true);
                    try {
                      // Validate both forms
                      await infoFormRef.current.submitForm();
                      await userFormRef.current.submitForm();

                      // Check if both forms are valid
                      const isInfoValid = Object.keys(infoFormRef.current.errors || {}).length === 0;
                      const isUserValid = Object.keys(userFormRef.current.errors || {}).length === 0;

                      if (isInfoValid && isUserValid) {
                        // Check if there's a file in the form data (for avatar upload)
                        const hasFile = infoFormRef.current.values.file;

                        try {
                          // Combine all form values
                          const formValues = {
                            ...infoFormRef.current.values,
                            ...userFormRef.current.values,
                          };

                          // If there's a file, convert to FormData, otherwise use regular JSON
                          const payload = hasFile
                            ? simpleObjectToFormData(formValues)
                            : formValues;

                          if (isEditMode) {
                            const result = await updateAdmin({ id, ...payload });
                            console.log("Admin updated successfully:", result);
                            addToast("อัปเดตข้อมูลผู้ดูแลระบบเรียบร้อยแล้ว", { appearance: "success" });
                          } else {
                            const result = await addAdmin(payload);
                            console.log("Admin added successfully:", result);
                            addToast("เพิ่มผู้ดูแลระบบเรียบร้อยแล้ว", { appearance: "success" });
                          }
                          // Redirect to admin list
                          navigate("/admin");
                        } catch (error) {
                          console.error("Operation failed:", error);
                          addToast(isEditMode ? "ไม่สามารถอัปเดตข้อมูลผู้ดูแลระบบได้ กรุณาลองใหม่อีกครั้ง" : "ไม่สามารถเพิ่มผู้ดูแลระบบได้ กรุณาลองใหม่อีกครั้ง", { appearance: "error" });
                        }
                      } else {
                        // Show error message if any form is invalid
                        console.error("Form validation failed. Please check all fields.");
                        // Optionally, you can scroll to the first error field
                        const firstErrorElement = document.querySelector('.is-invalid, [class*="is-invalid"]');
                        if (firstErrorElement) {
                          firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                        }
                      }
                    } catch (error) {
                      console.error("Form submission error:", error);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }
                }}
              >
                {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
              </Button>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default withTranslation()(AdminForm);
